// src/lib/sam-api/client.ts - AUTH BYPASS FIX
import { supabase } from '../supabase';
import { SamContractResponse } from '../../types/sam';
import { SamContractTransformer } from './transformer';

export interface SamApiOptions {
  page?: number;
  size?: number;
  postedFrom?: string;
  postedTo?: string;
  keyword?: string;
  naicsCode?: string;
  agency?: string;
  state?: string;
  debug?: boolean;
}

export interface PaginatedResults<T> {
  items: T[];
  totalRecords: number;
  page: number;
  size: number;
  hasMore: boolean;
}

class SamApiCache {
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static get<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  static set<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  static clear(): void {
    this.cache.clear();
  }
}

export class SamApiClient {
  private debug: boolean;
  private logger: Console;

  constructor(debug = false) {
    this.debug = debug;
    this.logger = console;
  }

  /**
   * FIXED: Main request method with auth bypass
   */
  private async request<T>(endpoint: string, params?: URLSearchParams): Promise<T> {
    const cacheKey = `sam-api:${endpoint}:${params?.toString() || ''}`;

    // Check cache first (if not in debug mode)
    if (!this.debug) {
      const cachedData = SamApiCache.get<T>(cacheKey);
      if (cachedData) {
        this.logger.debug('Cache hit:', cacheKey);
        return cachedData;
      }
    }

    // Build proxy URL for Supabase Edge Function
    const proxyParams = new URLSearchParams({
      endpoint,
      params: params?.toString() || ''
    });
    const url = `${supabase.supabaseUrl}/functions/v1/sam-api-proxy?${proxyParams.toString()}`;

    // FIXED: Get auth token with timeout/fallback
    let token = '';
    try {
      this.logger.debug('🔑 Attempting to get auth session...');
      
      // Add timeout to auth call
      const authPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth timeout')), 3000)
      );
      
      const sessionResult = await Promise.race([authPromise, timeoutPromise]) as any;
      token = sessionResult?.data?.session?.access_token || '';
      
      if (token) {
        this.logger.debug('✅ Auth token retrieved successfully');
      } else {
        this.logger.warn('⚠️ No auth token available, proceeding without auth');
      }
    } catch (authError) {
      this.logger.warn('⚠️ Auth failed, proceeding without auth:', authError);
      // Continue without auth - the edge function should still work
    }

    if (this.debug) {
      this.logger.info('🔍 Making request to SAM API proxy:', url);
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'apikey': supabase.supabaseKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('❌ SAM API proxy error response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        throw new Error(`SAM API proxy request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (this.debug) {
        this.logger.info('📦 SAM API proxy response received:', {
          hasData: !!data,
          totalRecords: data?.totalRecords || 0,
          opportunitiesCount: data?.opportunitiesData?.length || 0
        });
      }

      // Cache the result
      if (!this.debug) {
        SamApiCache.set(cacheKey, data);
      }

      return data as T;
    } catch (error) {
      this.logger.error('💥 SAM API client request failed:', error);
      throw error;
    }
  }

  /**
   * RESTORED: Your original searchContracts method with working transformer
   */
  async searchContracts(options: Partial<SamApiOptions> = {}): Promise<PaginatedResults<SamContractResponse>> {
    const {
      page = 0,
      size = 100,
      postedFrom,
      postedTo,
      keyword,
      naicsCode,
      agency,
      state,
      debug = false
    } = options;

    // Set debug mode
    if (debug) this.setDebug(true);

    // Build search parameters
    const searchParams = new URLSearchParams();
    
    // Required pagination
    searchParams.set('limit', size.toString());
    searchParams.set('offset', (page * size).toString());

    // Date filters - if not provided, use last 30 days
    if (postedFrom && postedTo) {
      searchParams.set('postedFrom', postedFrom);
      searchParams.set('postedTo', postedTo);
    } else {
      // Default to last 30 days with proper SAM.gov date format
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      searchParams.set('postedFrom', this.formatDateForSAM(thirtyDaysAgo));
      searchParams.set('postedTo', this.formatDateForSAM(today));
    }

    // Optional filters
    if (keyword) searchParams.set('keyword', keyword);
    if (naicsCode) searchParams.set('naicsCode', naicsCode);
    if (agency) searchParams.set('agency', agency);
    if (state) searchParams.set('state', state);

    // IMPORTANT: Filter out award notices - only get active solicitations
    searchParams.set('noticetype', 'o,p,k,r,g,s'); // Exclude award notices (a,i,u)
    
    if (this.debug) {
      this.logger.info('🔍 SAM API search parameters:', Object.fromEntries(searchParams.entries()));
    }

    try {
      // Make request through edge function
      const rawResponse = await this.request<any>('/search', searchParams);

      if (this.debug) {
        this.logger.info('📦 Raw SAM.gov response structure:', {
          keys: Object.keys(rawResponse || {}),
          totalRecords: rawResponse?.totalRecords,
          opportunitiesDataLength: rawResponse?.opportunitiesData?.length,
          firstOpportunity: rawResponse?.opportunitiesData?.[0] ? 'Present' : 'Missing'
        });
      }

      const totalRecords = rawResponse?.totalRecords || 0;
      const opportunitiesData = rawResponse?.opportunitiesData || [];

      if (opportunitiesData.length === 0) {
        this.logger.warn('⚠️ No opportunities data received from SAM.gov API');
        return {
          items: [],
          totalRecords: 0,
          page,
          size,
          hasMore: false
        };
      }

      // CRITICAL: Use your working SamContractTransformer with Promise resolution
      this.logger.info(`🔄 Transforming ${opportunitiesData.length} contracts using SamContractTransformer`);
      const transformPromises = opportunitiesData.map(async (opportunity: any) => {
        try {
          const transformed = await SamContractTransformer.transform(opportunity);
          return transformed;
        } catch (transformError) {
          this.logger.error('❌ Transform error for opportunity:', opportunity?.noticeId, transformError);
          // Return a basic contract if transform fails
          return {
            noticeId: opportunity?.noticeId || 'unknown',
            title: opportunity?.title || 'Unknown Title',
            agency: { name: opportunity?.organizationName || 'Unknown Agency' },
            description: opportunity?.description || '',
            error: 'Transform failed'
          };
        }
      });

      // FIXED: Resolve all transform promises
      const transformedContracts = await Promise.all(transformPromises);

      this.logger.info(`✅ Successfully transformed ${transformedContracts.length} contracts`);

      // Log first transformed contract for verification
      if (transformedContracts.length > 0 && this.debug) {
        const sample = transformedContracts[0];
        this.logger.info('🔍 First transformed contract sample:', {
          noticeId: sample.noticeId,
          title: sample.title?.substring(0, 50) + '...',
          agency: typeof sample.agency === 'object' ? sample.agency.name : sample.agency,
          naicsCode: sample.naicsCode,
          hasDescription: !!sample.description && sample.description.length > 100,
          hasContactEmail: !!(sample.pointOfContact?.[0]?.email || sample.contractingOfficer?.email),
          hasFunding: !!(sample.award_amount || sample.total_contract_value),
          hasDocuments: (sample.resourceLinks?.length || 0) > 0
        });
      }

      return {
        items: transformedContracts, // Use proper transformed contracts
        totalRecords,
        page,
        size,
        hasMore: (page + 1) * size < totalRecords
      };

    } catch (error) {
      this.logger.error('💥 Search contracts failed:', error);
      throw new Error(`Failed to search contracts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Format date for SAM.gov API (MM/dd/yyyy format)
   */
  private formatDateForSAM(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Clear API cache
   */
  clearCache(): void {
    SamApiCache.clear();
  }

  /**
   * Enable or disable debug mode
   */
  setDebug(debug: boolean): void {
    this.debug = debug;
  }
}

// Export singleton instance
export const samApiClient = new SamApiClient();
export const samApi = samApiClient;